<template>
  <div class="auth-container">
    <div class="auth-card">
      <div class="auth-header">
        <h1 class="auth-title">Welcome Back</h1>
        <p class="auth-subtitle">Log in to access FoodCycle.ai</p>
      </div>

      <form @submit.prevent="login" class="auth-form">
        <!-- Email Field -->
        <div class="form-field">
          <label for="email">Email</label>
          <div class="input-wrapper">
            <i class="bi bi-envelope input-icon"></i>
            <input
              type="email"
              id="email"
              v-model="email"
              placeholder="Enter your email"
              required
            />
          </div>
        </div>

        <!-- Password Field -->
        <div class="form-field">
          <label for="password">Password</label>
          <div class="input-wrapper">
            <i class="bi bi-lock input-icon"></i>
            <input
              :type="showPassword ? 'text' : 'password'"
              id="password"
              v-model="password"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              class="toggle-password"
              @click="showPassword = !showPassword"
            >
              <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
            </button>
          </div>
        </div>

        <!-- Error message -->
        <div v-if="errorMessage" class="error-message">
          <i class="bi bi-exclamation-triangle"></i>
          {{ errorMessage }}
        </div>

        <!-- Success message -->
        <div v-if="resetMessage" class="success-message">
          <i class="bi bi-check-circle"></i>
          {{ resetMessage }}
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          class="auth-btn"
          :disabled="isLoading"
        >
          <span v-if="!isLoading">Log In</span>
          <div v-else class="spinner-border spinner-border-sm text-light" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </button>

        <div class="forgot-password">
          <a href="#" @click.prevent="requestPasswordReset" class="auth-link">Forgot password?</a>
        </div>

        <!-- Development Mode Banner wurde entfernt -->

        <div class="auth-footer">
          <p>
            Don't have an account?
            <router-link to="/register" class="auth-link">Register</router-link>
          </p>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '../stores/auth';

export default {
  name: 'LogInMask',
  setup() {
    const authStore = useAuthStore();
    const router = useRouter();
    const route = useRoute();

    const email = ref('');
    const password = ref('');
    const showPassword = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const resetMessage = ref('');
    // isDevMode und useDemoLogin wurden entfernt, da das Banner entfernt wurde

    const login = async () => {
      try {
        isLoading.value = true;
        errorMessage.value = '';
        resetMessage.value = '';

        await authStore.login(email.value, password.value);

        // Redirect to the intended route or to the main tool page
        const redirectPath = route.query.redirect || '/tool';
        router.push(redirectPath);
      } catch (error) {
        console.error('Login error:', error);
        errorMessage.value = error?.response?.data?.message || 'Login failed. Please check your credentials.';
      } finally {
        isLoading.value = false;
      }
    };

    const requestPasswordReset = async () => {
      if (!email.value) {
        errorMessage.value = 'Please enter your email first.';
        return;
      }
      try {
        isLoading.value = true;
        errorMessage.value = '';
        await authStore.requestPasswordReset(email.value);
        resetMessage.value = 'Password reset link sent. Check your email.';
      } catch (error) {
        console.error('Password reset request error:', error);
        resetMessage.value = '';
        errorMessage.value = error?.response?.data?.message || 'Password reset failed.';
      } finally {
        isLoading.value = false;
      }
    };

    // useDemoLogin Funktion wurde entfernt

    return {
      email,
      password,
      showPassword,
      isLoading,
      errorMessage,
      resetMessage,
      // isDevMode entfernt
      login,
      requestPasswordReset,
      // useDemoLogin entfernt
    };
  }
};
</script>

<style scoped>
/* Styles bleiben unverändert - hier zur Vollständigkeit gekürzt */
.auth-container {
  width: 100%;
  max-width: 450px;
  padding: 2rem 1.5rem;
}

.auth-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05), 0 5px 10px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(135, 177, 64, 0.1);
  padding: 2.5rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.auth-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: #87B140;
}

.auth-header {
  text-align: center;
  margin-bottom: 2rem;
}

.auth-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
}

.auth-subtitle {
  font-size: 1rem;
  color: #666;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-field {
  margin-bottom: 0.5rem;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #555;
  font-size: 0.95rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.input-icon {
  position: absolute;
  left: 10px;
  color: #87b140;
  font-size: 1.1rem;
  pointer-events: none;
}

input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

input:focus {
  outline: none;
  border-color: #87b140;
  box-shadow: 0 0 0 2px rgba(135, 177, 64, 0.2);
}

.toggle-password {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  transition: color 0.2s ease;
}

.toggle-password:hover {
  color: #333;
}

.error-message {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.success-message {
  background-color: rgba(40, 167, 69, 0.1);
  color: #28a745;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.auth-btn {
  background-color: #87B140;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
}

.auth-btn:hover:not(:disabled) {
  background-color: #75a236;
  transform: translateY(-1px);
}

.auth-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.forgot-password {
  text-align: right;
  margin-top: 0.5rem;
}

.auth-footer {
  text-align: center;
  color: #666;
  font-size: 0.95rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.auth-link {
  color: #87B140;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
}

.auth-link:hover {
  color: #75a236;
  text-decoration: underline;
}

/* Dev-Mode-Banner Styles wurden entfernt, da das Element entfernt wurde */

@media (max-width: 576px) {
  .auth-container {
    padding: 1.5rem 1rem;
  }

  .auth-card {
    padding: 1.75rem;
  }

  .auth-title {
    font-size: 1.5rem;
  }

  input {
    padding: 10px 10px 10px 36px;
  }

  .input-icon {
    font-size: 1rem;
  }
}
</style>